/**
 * Bold Workspace — Widget Bridge srcdoc builder
 *
 * Self-contained implementation (no SDK import) so Next.js Turbopack
 * can resolve this file without crossing the project root boundary.
 *
 * The SDK source (`bold-workspace-sdk/src/widget/inline.ts`) is the
 * canonical version; this file mirrors it for host-side use.
 */

export interface WidgetSrcDocOptions {
  /** Current host theme for immediate application ('light' | 'dark') */
  theme?: 'light' | 'dark';
  /** Widget ID (used in BOLD_WIDGET_GET_CONTEXT responses) */
  widgetId?: string;
}

/**
 * Wraps raw widget HTML/JS code into a full srcdoc HTML document
 * with the BoldWidgetBridge auto-injected as the global `boldWidget`.
 *
 * @param userCode  - The raw HTML/JS snippet the developer wrote
 * @param options   - Theme and widgetId hints for immediate context
 * @returns Full HTML string to use as `<iframe srcDoc={...}>`
 */
export function buildWidgetSrcDoc(userCode: string, options: WidgetSrcDocOptions = {}): string {
  const theme = options.theme ?? 'light';
  const widgetId = options.widgetId ?? '';

  return `<!DOCTYPE html>
<html data-theme="${theme}" lang="pt-BR">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    :root {
      --bold-blue: #004EB1;
      --bold-bg: #ffffff;
      --bold-fg: #18181b;
      --bold-muted: #71717a;
      --bold-border: #e4e4e7;
      --bold-radius: 6px;
    }
    [data-theme="dark"] {
      --bold-bg: #09090b;
      --bold-fg: #f4f4f5;
      --bold-muted: #a1a1aa;
      --bold-border: #27272a;
    }
    body {
      margin: 0;
      padding: 12px;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Roboto, "Helvetica Neue", Arial, sans-serif;
      font-size: 13px;
      color: var(--bold-fg);
      background: var(--bold-bg);
      transition: color 0.15s ease, background-color 0.15s ease;
    }
    button {
      font-family: inherit;
      font-size: 11px;
      font-weight: 600;
      padding: 4px 10px;
      border-radius: var(--bold-radius);
      border: 1px solid var(--bold-border);
      background: var(--bold-bg);
      color: var(--bold-fg);
      cursor: pointer;
      transition: all 0.15s ease;
    }
    button:hover { opacity: 0.8; }
    button:active { transform: scale(0.96); }
    button.primary { background: var(--bold-blue); color: #fff; border-color: transparent; }
    input, textarea, select {
      font-family: inherit;
      font-size: 12px;
      padding: 5px 8px;
      border-radius: var(--bold-radius);
      border: 1px solid var(--bold-border);
      background: var(--bold-bg);
      color: var(--bold-fg);
      width: 100%;
    }
    input:focus, textarea:focus { outline: 2px solid var(--bold-blue); outline-offset: 1px; }
  </style>
</head>
<body>

<!-- BoldWidgetBridge (auto-injected by Bold Workspace) -->
<script>
(function () {
  'use strict';
  var _widgetId = ${JSON.stringify(widgetId)};
  var _pending = {};
  var _themeHandlers = [];
  var _resizeHandlers = [];
  var _languageHandlers = [];
  function _send(msg) { window.parent.postMessage(msg, '*'); }
  function _req(reqType) {
    return new Promise(function (resolve, reject) {
      var id = Math.random().toString(36).slice(2) + Date.now().toString(36);
      var timer = setTimeout(function () {
        delete _pending[id];
        reject(new Error('boldWidget: "' + reqType + '" timed out.'));
      }, 5000);
      _pending[id] = {
        resolve: function (v) { clearTimeout(timer); resolve(v); },
        reject:  function (e) { clearTimeout(timer); reject(e);  }
      };
      _send({ type: reqType, requestId: id, widgetId: _widgetId });
    });
  }
  window.addEventListener('message', function (ev) {
    var msg = ev.data;
    if (!msg || typeof msg.type !== 'string' || !msg.type.startsWith('BOLD_WIDGET_')) return;
    if (msg.type === 'BOLD_WIDGET_THEME_CHANGED') {
      document.documentElement.dataset.theme = msg.payload;
      _themeHandlers.forEach(function (h) { try { h(msg.payload); } catch(e){} });
      return;
    }
    if (msg.type === 'BOLD_WIDGET_RESIZED') {
      _resizeHandlers.forEach(function (h) { try { h(msg.payload); } catch(e){} });
      return;
    }
    if (msg.type === 'BOLD_WIDGET_LANGUAGE_CHANGED') {
      document.documentElement.lang = msg.payload;
      _languageHandlers.forEach(function (h) { try { h(msg.payload); } catch(e){} });
      return;
    }
    if (msg.requestId && _pending[msg.requestId]) {
      var p = _pending[msg.requestId]; delete _pending[msg.requestId];
      if (msg.type === 'BOLD_WIDGET_ERROR' || msg.error) { p.reject(new Error(msg.error || 'Bridge error')); }
      else { p.resolve(msg.payload); }
    }
  });
  window.boldWidget = {
    getContext:    function () { return _req('BOLD_WIDGET_GET_CONTEXT'); },
    getSSOToken:   function () { return _req('BOLD_WIDGET_GET_SSO_TOKEN'); },
    getUsers:      function () { return _req('BOLD_WIDGET_GET_USERS'); },
    resize:        function (s) { _send({ type: 'BOLD_WIDGET_RESIZE',     payload: s,             widgetId: _widgetId }); },
    close:         function ()  { _send({ type: 'BOLD_WIDGET_CLOSE',                              widgetId: _widgetId }); },
    minimize:      function ()  { _send({ type: 'BOLD_WIDGET_MINIMIZE',                           widgetId: _widgetId }); },
    setTitle:      function (t) { _send({ type: 'BOLD_WIDGET_SET_TITLE',  payload: { title: t }, widgetId: _widgetId }); },
    setReady:      function ()  { _send({ type: 'BOLD_WIDGET_SET_READY',                          widgetId: _widgetId }); },
    sendNotification: function (n) { _send({ type: 'BOLD_WIDGET_SEND_NOTIFICATION', payload: n, widgetId: _widgetId }); },
    onThemeChange: function (fn) { _themeHandlers.push(fn); return function () { _themeHandlers = _themeHandlers.filter(function(h){ return h !== fn; }); }; },
    onResize:      function (fn) { _resizeHandlers.push(fn); return function () { _resizeHandlers = _resizeHandlers.filter(function(h){ return h !== fn; }); }; },
    onLanguageChange: function (fn) { _languageHandlers.push(fn); return function () { _languageHandlers = _languageHandlers.filter(function(h){ return h !== fn; }); }; },
    destroy:       function ()  { _themeHandlers = []; _resizeHandlers = []; _languageHandlers = []; }
  };
})();
</script>
<!-- End BoldWidgetBridge -->

${userCode}

</body>
</html>`;
}

